import { apiClient } from '@/lib/api-client'
import { apiErrorMessage } from '@/lib/api-client'
import type { IngestStatus } from '@/types/ingest'

export interface UploadOutcome {
  filename: string
  status: 'queued' | 'skipped' | 'failed'
  document_id?: number
  title?: string
  reason?: string
  message?: string
  /** Title of the document this file duplicates, when it was skipped. */
  duplicate_of?: string
}

/**
 * SHA-256 of a file, matching what the server stores as `content_sha256`.
 *
 * `crypto.subtle` needs a secure context; over plain HTTP it is undefined, so
 * this returns null and the caller falls back to uploading and letting the
 * server decide. Hashing reads the file into memory, so callers do one at a
 * time rather than mapping over the whole drop.
 */
export async function fileHash(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null
  try {
    const buf = await file.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

/** Which of these hashes the collection already holds, as hash → document title. */
export async function checkHashes(
  collectionId: number,
  hashes: string[],
): Promise<Record<string, string>> {
  if (!hashes.length) return {}
  try {
    const { data } = await apiClient.post<{ known: Record<string, string> }>(
      `/collections/${collectionId}/documents/check`,
      { hashes },
    )
    return data.known ?? {}
  } catch {
    // Never let the optimisation block the upload; the server checks again.
    return {}
  }
}

/** Upload one PDF. Resolves once its row and text are stored, not once processed. */
async function uploadOne(
  collectionId: number,
  file: File,
  opts: { domains?: string; documentType?: string },
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<UploadOutcome> {
  const form = new FormData()
  form.append('file', file)
  form.append('domains', opts.domains ?? '')
  form.append('document_type', opts.documentType ?? 'other')

  const { data } = await apiClient.post(`/collections/${collectionId}/documents`, form, {
    // The shared client defaults every request to application/json. Sending
    // that with a FormData body makes the server reject the whole thing as
    // unparseable, so clear it and let the browser write the multipart header
    // with its boundary. (The preview upload avoids this by using fetch.)
    headers: { 'Content-Type': undefined },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
    },
    timeout: 0,
    signal,
  })
  return { filename: file.name, ...data }
}

/**
 * Send a dropped batch as one request per file, in order.
 *
 * Not one request with every file attached, which is what this used to be. That
 * shape failed twice over: nothing existed server-side until the whole batch
 * had been transferred, so the list sat empty behind a progress bar for the
 * entire upload; and the combined body ran into the proxy's size limit, which
 * failed the whole drop rather than one file.
 *
 * Sequential rather than parallel — the server processes one file at a time
 * anyway, so racing the uploads would only compete for the same bandwidth and
 * make each row's arrival less predictable.
 */
export async function uploadFiles(
  collectionId: number,
  files: File[],
  opts: {
    domains?: string
    documentType?: string
    signal?: AbortSignal
    /** After each file lands, so the caller can refresh the list. */
    onFileDone?: (outcome: UploadOutcome, index: number) => void
    /** Transfer progress of the file currently going up. */
    onProgress?: (index: number, pct: number) => void
  } = {},
): Promise<UploadOutcome[]> {
  const out: UploadOutcome[] = []
  for (let i = 0; i < files.length; i++) {
    if (opts.signal?.aborted) break
    let outcome: UploadOutcome
    try {
      outcome = await uploadOne(
        collectionId,
        files[i],
        opts,
        (pct) => opts.onProgress?.(i, pct),
        opts.signal,
      )
    } catch (e) {
      // One rejected file must not cost the rest of the drop.
      outcome = { filename: files[i].name, status: 'failed', message: apiErrorMessage(e) }
    }
    out.push(outcome)
    opts.onFileDone?.(outcome, i)
  }
  return out
}

export async function getIngestStatus(collectionId: number): Promise<IngestStatus> {
  const { data } = await apiClient.get<IngestStatus>(
    `/collections/${collectionId}/documents/ingest`,
  )
  return data
}

/** Stop after the file currently being processed; finished files are kept. */
export async function cancelIngest(collectionId: number): Promise<{ cancelled: boolean }> {
  const { data } = await apiClient.delete(`/collections/${collectionId}/documents/ingest`)
  return data
}
